import React, { Component, useState } from 'react';
import { AsyncSpinnerButton, FixedButton } from 'components/Button';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { modalPathFor } from 'config/paths';
import LinkButton from 'components/LinkButton';
import { useTranslation } from 'react-i18next';
import { CenteredActivityContainer } from 'components/ActivityContainer';
import { ActivityTab, ActivityTabs } from 'components/BottomNav';
import { AsyncCardAction, ListCard, ListCardAction, ListCardTitle, ListCardActionLink } from 'components/List';
import { Loader } from 'components/LoadingHOC';
import * as _ from 'lib/utilities';
import requestStateHandler from 'components/RequestStateHandler';
import Collapse from 'components/Collapse';
import { MainHeading, SlightEmphasisIconNote } from 'components/Typography';
import classnames from 'classnames';
import { TagButtons } from 'components/Form';
import AnimTo from 'components/AnimTo';
import { ChildDropdown, DropdownDelete, DropdownItem, DropdownLink, MultiLevelDropdown } from 'components/Dropdown';
import { useHistory } from 'react-router';
import { useLocation } from 'react-router-dom';
import { TrainerSelectModal, trainerSelectSuffix } from './TrainerSelectModal';
import { UserIcon } from './Utilities';
import { DropdownLinkTPB } from './ProRequiredButton';
import { TRAINING } from 'config/settings';
import { exportRoutinePDFSuffix } from './ExportPDFModal';
import { previewRoutinePdfUrlFor } from 'lib/api';
import { RecordTags} from 'partials/RecordUtilities';

const TabLabel = ({ importCount, label }) => {

    return (
        <React.Fragment>{label}{importCount > 0 && (<AnimTo top={'150px'} left={'150px'} tag='b' className="primary-text"> +{importCount}</AnimTo>)}</React.Fragment>
    )
}

export const RecordTabs = ({ importCount, notTop, basePath }) => {
    const { t } = useTranslation();
    const paths = [`${basePath}/personal`,`${basePath}/db`,`${basePath}/team`];
    const labels = [<TabLabel key="personal" label={t("Mine")} importCount={importCount.personal} />,<TabLabel key="db" label={t("Database")} importCount={importCount.db} />,t("Team")];

    return (
        <ActivityTabs className={classnames({ 'not-top': notTop, 'off-white': notTop, 'bottom-border': notTop })}>
            {paths.map((path,index) => <ActivityTab id={"routines-tab-link-"+index} path={path} label={labels[index]} key={path} />)}
        </ActivityTabs>
    )
}

const ImportDropdown = ({ record, importRecord, t }) => {
    return (
        <MultiLevelDropdown
            options={{ constrainWidth: false, alignment: 'right', coverTrigger: false, closeOnClick: true, closeOnChildClick: true }}
            triggerRender={({ ref, target }) => {
                return (
                    <ListCardAction ref={ref} data-target={target} id={`import-team-wrt-btn-${record.id}`}>
                        <FontAwesomeIcon icon={['far','copy']} />
                    </ListCardAction>
                )
            }}
        >
            <DropdownItem
                label={t("To My Account")}
                onClick={() => importRecord(record.id,false)}
                id={`import-my-account-btn-${record.id}`}
                icon="user"
            />
            <DropdownItem
                label={t("To Database")}
                onClick={() => importRecord(record.id, false, { forDb: true })}
                id={`import-db-btn-${record.id}`}
                icon="database"
            />
        </MultiLevelDropdown>
    )
}

export const MyRecordDropdown = ({ record, importRecord, newDestroy, publishRecord, t, allowsDbImport, tabType }) => {
    const { pathname } = useLocation();

    return (
        <MultiLevelDropdown
            options={{ constrainWidth: false, alignment: 'right', coverTrigger: false, closeOnClick: true, closeOnChildClick: true }}
            triggerRender={({ ref, target }) => {
                return (
                    <ListCardAction ref={ref} data-target={target} id={`wrt-dropdown-btn-${record.id}`}>
                        <FontAwesomeIcon icon="ellipsis-h" />
                    </ListCardAction>
                )
            }}
        >
            {tabType === 'db' && (!record.published) && publishRecord &&
                <DropdownItem
                    onClick={() => publishRecord(record.id)}
                    label={t("Publish")}
                    icon="paper-plane"
                    id={`wrt-publish-btn-${record.id}`}
                />
            }
            <DropdownLink
                to={record.editPath(pathname)}
                id={`wrt-edit-btn-${record.id}`}
                icon={['far','edit']}
                label={t("Edit")}
            />
            <DropdownLink
                to={record.assignToClientsPath()}
                id={`wrt-assign-btn-${record.id}`}
                icon='users'
                label={t("Assign to Client(s)")}
            />
            {record.isRoutine() && (
                <DropdownLinkTPB
                    type={TRAINING}
                    id="pdf-routine-btn"
                    label={t("Export PDF")}
                    icon={['far','file-export']}
                    to={modalPathFor(exportRoutinePDFSuffix, pathname, { routineId: record.id })}
                    allowUninit={false}
                />
            )}
            {allowsDbImport ? (
                <ChildDropdown
                    label={t("Copy")}
                    icon={['far','copy']}
                    triggerId={`wrt-copy-btn-${record.id}`}
                >
                    <DropdownItem
                        label={t("To My Account")}
                        onClick={() => importRecord(record.id, tabType === 'personal')}
                        id={`wrt-copy-my-account-btn-${record.id}`}
                        icon={"user"}
                    />
                    <DropdownItem
                        label={t("To Database")}
                        onClick={() => importRecord(record.id, tabType === 'db', { forDb: true })}
                        id={`wrt-copy-db-btn-${record.id}`}
                        icon={"database"}
                    />
                </ChildDropdown>
            ) : (
                <DropdownItem
                    onClick={() => importRecord(record.id, tabType === 'personal')}
                    id={`wrt-copy-btn-${record.id}`}
                    icon={['far','copy']}
                    label={t("Copy")}
                />
            )}
            <DropdownDelete
                action={() => newDestroy()}
                label={t("Delete")}
                icon="times"
                id={`wrt-delete-btn-${record.id}`}
            />
        </MultiLevelDropdown>
    )
}

let RecordListing = ({ record, tabType, importRecord, t, destroy, PersonalMenu, hasPersonalMenu, hasDbImport, publishRecord }) => {

    const [expanded,setExpanded] = useState(false);
    const { pathname } = useLocation();
    const unowned = (tabType === 'team' || tabType === 'db');
    const showTags = !!record.getTagList;

    const newDestroy = () => {
        const newRecord = new record.constructor({ ...record, inactive: true });
        destroy(newRecord);
    }

    if(!record.isActive()) {
        return '';
    }

    return (
        <ListCard className="wrt-list-card" collapse={unowned ? (<Collapse isOpen={expanded}>
            <div className="ma5"><SlightEmphasisIconNote text={record.resolvedDescription(t)} /></div>
        </Collapse>) : null}>
            <ListCardTitle withTags linkTo={record.editPath(pathname)}>
                {showTags && <RecordTags record={record} listCard />}
                {record.resolvedName(t)}
            </ListCardTitle>
            {record.isRoutine() && (<ListCardActionLink to={previewRoutinePdfUrlFor({ id: record.id},true,false)} target="_blank">
                <FontAwesomeIcon icon={"info-circle"} />
            </ListCardActionLink>)}
            {hasPersonalMenu && <PersonalMenu record={record} t={t} newDestroy={newDestroy} importRecord={importRecord} tabType={tabType} publishRecord={publishRecord} />}
            {unowned && !hasPersonalMenu && hasDbImport && <ImportDropdown record={record} importRecord={importRecord} t={t} />}
            {unowned && !hasPersonalMenu && !hasDbImport && <AsyncCardAction id={`import-team-wrt-btn-${record.id}`} icon={['far','file-import']} load={() => importRecord(record.id,tabType === 'personal')} label={t('Import')} />}
            {!record.isRoutine() && unowned && (<ListCardAction onClick={() => setExpanded(!expanded )}>
                <FontAwesomeIcon icon={"info-circle"} />
            </ListCardAction>)}
        </ListCard>
    )
}

RecordListing = requestStateHandler({ destroy: 'record' })(RecordListing)



export const TrainerSelectButton = ({ trainer, selTrainerId }) => {
    const selTrainer = trainer.trainerById(selTrainerId);
    const { pathname } = useLocation();
    const { t } = useTranslation();

    return (
        <LinkButton rounded outlined color="primary" className="show-overflow" to={modalPathFor(trainerSelectSuffix,pathname)}>
            <FontAwesomeIcon icon={['far','filter']} />
            {t('Filter')}
            {selTrainer && (<UserIcon user={selTrainer} className="xs usr-filter" />)}
        </LinkButton> 
    )
}

const TrainerRecordButtons = ({ trainer, selTrainerId, selectTrainer }) => {
    const { t } = useTranslation();
    const trainers = trainer.allOtherTrainers();
    const usePopup = trainer.usePopupForTrainerSelect();
    const col = [{ text: t('All'), value: '' }, ...trainers.map(trainer => ({ text: trainer.initialsName(), value: trainer.id }))]
    const classNames = classnames("added-people-wrapper wrap fake-pad")
    const sharedProps = { setFieldTouched: () => {}, setFieldValue: (name,value) => selectTrainer(value), name: 'trainerId', values: { trainerId: selTrainerId } }

    if(trainer.teamSize <= 1) {
        return '';
    }

    return (
        <div>
            <div className="font-grey no-wrap">{t('Team member')}:</div>
            <div className={classNames}>
                {!usePopup && (<TagButtons
                    {...sharedProps}
                    col={col}
                    btnId={(val) => `toggle-trainer-${val}`}
                    single
                    buttonProps={{ className: 'btn-no-shadow ma2' }}
                />)}
                {usePopup && (
                    <TrainerSelectButton 
                        trainer={trainer}
                        selTrainerId={selTrainerId}
                    />
                )}
            </div>
        </div>
    )

}

const RecordsListCore = ({ records, tabType, importRecord, destroy, publishRecord, hasPersonalMenu, hasDbImport, PersonalMenu }) => {
    const { t } = useTranslation();


    return (<React.Fragment>
            {_.sortBy(records,wrt => wrt.resolvedName(t)).map(rec => (
                <RecordListing
                    key={rec.id}
                    record={rec}
                    tabType={tabType}
                    t={t}
                    importRecord={importRecord}
                    destroy={destroy}
                    publishRecord={publishRecord}
                    PersonalMenu={PersonalMenu}
                    hasPersonalMenu={hasPersonalMenu}
                    hasDbImport={hasDbImport}
                />
            ))}
    </React.Fragment>)
}

const RecordsList = ({ records, recordType: type, importRecord, destroy, publishRecord, trainer, selTrainerId, selectTrainer, isLarge, recordNameP, recordNameS, NewModal, EditModal, ExportModal, PersonalMenu, loadRecords, topMessage, bottomMessage, customDbAllowed }) => {
    const { t } = useTranslation();
    const history = useHistory();
    const { pathname } = useLocation();
    const finalRecords = _.filter(records, rec => !rec.inactive);
    const selTrainer = trainer.trainerById(selTrainerId);
    const newPath = modalPathFor('/new',pathname);
    const incrementalLoading = type === 'team' && trainer.hasLargeTeam();
    const canMakeNew = (type === 'personal' || (type === 'db' && trainer.isTrainerAdmin() && customDbAllowed))
    const hasPersonalMenu = canMakeNew;
    const hasDbImport = trainer.isTrainerAdmin() && customDbAllowed;
    const listProps = { records: finalRecords, tabType: type, importRecord, destroy, publishRecord, PersonalMenu, hasPersonalMenu, hasDbImport };

    let emptyMessage = type === 'personal' ? t('empty records tip',{ record_name: recordNameP }) : (_.isBlank(selTrainerId) ? t('empty team records', { record_name: recordNameP }) : t('empty trainer records', { name: selTrainer.initialsName(), record_name: recordNameP }))

    if(type === 'team' && trainer.hasLargeTeam() && _.isBlank(selTrainer)) {
        emptyMessage = t("select to view routines");
    }
    let emtpyBtn = type === 'personal' ? (
        <AsyncSpinnerButton
            buttonProps={{
                noShadow: true,
                color: 'primary',
                label: t('Import Sample'),
                id: "import-sample-btn"
            }}
            icon={['far','file-import']}
            action={() => importRecord()}
        />
    ) : null;

    return (
        <div className="mt15 mb150">
            {type === 'team' && (
                <React.Fragment>
                    <TrainerRecordButtons trainer={trainer} selTrainerId={selTrainerId} selectTrainer={selectTrainer} />
                    <TrainerSelectModal trainer={trainer} initialTrainerId={selTrainerId} submit={({ trainerId }) => selectTrainer(trainerId)} excludeSelf={true} noAll />
                </React.Fragment>
            )}
            {canMakeNew && (
                <LinkButton
                    to={newPath}
                    rounded
                    color="primary"
                    outlined={type === 'personal' && finalRecords.length === 0}
                    className="hide-on-med-and-down"
                >
                    <FontAwesomeIcon icon={'plus'}></FontAwesomeIcon> <span>{t("New")}</span>
                </LinkButton>
            )}
            {topMessage && <div className="mt10 mb10">{topMessage}</div>}
            {finalRecords.length === 0 && _.isBlank(topMessage) && (
                <div className="mb10 mt10">
                    <SlightEmphasisIconNote text = {emptyMessage} btn={emtpyBtn} />
                </div>
            )}
            {!incrementalLoading && (
                <RecordsListCore {...listProps} />
            )}
            {incrementalLoading && (
                <Loader
                    key={selTrainerId}
                    successComponent={RecordsListCore}
                    type="padded"
                    load={() => loadRecords({ type, trainerId: selTrainerId })}
                    preloaded={() => (finalRecords.length !== 0)}
                    alwaysLoad
                    {...listProps}
                />
            )}
            {bottomMessage && (
                <div className="mt10 text-center">
                    {bottomMessage}
                </div>
            )}
            {canMakeNew && (<NewModal forDatabase={type === 'db'} />)}
            {type === 'personal' && (
                <React.Fragment>
                    {EditModal && <EditModal />}
                    {ExportModal && <ExportModal />}
                    {!isLarge && <FixedButton id="new-routine-btn" icon="plus" onClick={() => history.push(newPath)} />}
                </React.Fragment>
            )}
        </div>
    )
}

class IndexPage extends Component {

    componentDidUpdate() {
        const { match: { params: { type }}, importCount, clearImports } = this.props;

        if(importCount[type] && importCount[type] > 0) {
            clearImports(type);
        }
    }

    render() {
        const { 
            Wrapper=CenteredActivityContainer, 
            trainer, 
            title, 
            dbRecords, 
            load, 
            history, 
            trainerId, 
            match: { params: { type }}, 
            recordName, 
            recordNameS,
            recordNameP,
            importRecord, 
            destroy, 
            selectTrainer, 
            addImport, 
            isLarge, 
            importCount,
            basePath,
            successComponent=RecordsList,
            NewModal,
            EditModal,
            PersonalMenu=MyRecordDropdown,
            ExportModal,
            hideTabs,
            topMessage,
            bottomMessage,
            customDbAllowed,
            publishRecord
        } = this.props;

        let records = trainer[`active${recordName}`]();
        const selTrainerId = trainer.resolvedTrainerForRecords(trainerId);
        if(type === 'db') {
            records = dbRecords;
        } else if(type === 'team') {
            records = trainer[`team${recordName}`](selTrainerId);
        }

        return (
            <div>
                {isLarge && (<CenteredActivityContainer className="mb0">
                    <MainHeading>{title}</MainHeading>
                    {!hideTabs && (<RecordTabs importCount={importCount} notTop basePath={basePath} />)}
                </CenteredActivityContainer>)}
                <Wrapper>
                    <Loader
                        key={type}
                        successComponent={successComponent}
                        type="page"
                        recordType={type}
                        isLarge={isLarge}
                        load={() => load({ type })}
                        loadRecords={load}
                        preloaded={() => (type === 'personal' || records.length !== 0)}
                        alwaysLoad
                        trainer={trainer}
                        selTrainerId={selTrainerId}
                        records={records}
                        importRecord={(id,popup,params) => {
                            return importRecord(id,popup,params).then(resp => {
                                if(!popup) {
                                    if(params && params.forDb) {
                                        addImport('db');
                                    } else {
                                        addImport('personal');
                                    }
                                }
                                return Promise.resolve(resp);
                            })
                        }}
                        selectTrainer={selectTrainer}
                        destroy={destroy}
                        history={history}
                        NewModal={NewModal}
                        EditModal={EditModal}
                        ExportModal={ExportModal}
                        PersonalMenu={PersonalMenu}
                        recordNameS={recordNameS}
                        recordNameP={recordNameP}
                        topMessage={topMessage}
                        bottomMessage={bottomMessage}
                        customDbAllowed={customDbAllowed}
                        publishRecord={publishRecord}
                    />
                </Wrapper>
            </div>
        );
    }
}

export default IndexPage;
